'use client';

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import {
  DEFAULT_AI_MODEL,
  findAiModelByKey,
  aiModelKey,
  type AiModelEntry,
} from '@ai/core/types/ai-types.ts';

interface ModelPreferencesContextValue {
  selectedModel: AiModelEntry;
  setSelectedModel: (m: AiModelEntry) => void;
}

const ModelPreferencesContext = createContext<ModelPreferencesContextValue | null>(null);

const STORAGE_KEY = 'qm-ai-model';

export function ModelPreferencesProvider({ children }: { children: ReactNode }) {
  const [selectedModel, setSelectedModelState] = useState<AiModelEntry>(DEFAULT_AI_MODEL);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      const found = findAiModelByKey(stored);
      if (found) setSelectedModelState(found);
    }
    setIsHydrated(true);
  }, []);

  const setSelectedModel = (m: AiModelEntry) => {
    setSelectedModelState(m);
    localStorage.setItem(STORAGE_KEY, aiModelKey(m));
  };

  return (
    <ModelPreferencesContext.Provider
      value={{
        selectedModel: isHydrated ? selectedModel : DEFAULT_AI_MODEL,
        setSelectedModel,
      }}
    >
      {children}
    </ModelPreferencesContext.Provider>
  );
}

export function useModelPreference(): ModelPreferencesContextValue {
  const context = useContext(ModelPreferencesContext);
  if (!context) {
    return {
      selectedModel: DEFAULT_AI_MODEL,
      setSelectedModel: () => {},
    };
  }
  return context;
}
